import { useLayoutEffect } from "react";
import { alleFenster } from "../components/windowManager/browserFenster/fensterRegister";

/**
 * Nächster scrollender Vorfahre eines Elements (oder `null`, wenn das Dokument
 * selbst scrollt). Wird auch außerhalb dieses Hooks gebraucht — z. B. um beim
 * Reiter-Wechsel an den Anfang zu springen.
 */
export function findScrollParent(el: HTMLElement | null): HTMLElement | null {
  let node = el?.parentElement ?? null;
  while (node) {
    const { overflowY } = window.getComputedStyle(node);
    if (/(auto|scroll|overlay)/.test(overflowY) && node.scrollHeight > node.clientHeight) {
      return node;
    }
    // Auch ein noch nicht überlaufender Container ist der richtige Scroller —
    // sonst würde die Höhe gegen den Viewport gerechnet, solange der Inhalt
    // gerade hineinpasst, und beim ersten Überlauf springt sie.
    if (/(auto|scroll|overlay)/.test(overflowY)) return node;
    node = node.parentElement;
  }
  return null;
}

/**
 * Platz, der UNTERHALB des Elements bis zum Ende des Scroll-Inhalts belegt ist:
 * nachfolgende Geschwister auf jeder Ebene (z. B. die Speicherleiste der
 * Kontaktdetails) plus das `padding-bottom` jedes Vorfahren bis einschließlich
 * des Scroll-Containers.
 *
 * Ohne diesen Abzug wird das Element zu hoch: Der Container bleibt um genau
 * diesen Betrag scrollbar, ein sticky Kopfbereich darüber überdeckt dann den
 * Anfang des Inhalts, und das Ende der Tabelle liegt unter der Fensterkante.
 */
function reservedBelow(el: HTMLElement, scroller: HTMLElement | null): number {
  let total = 0;
  let node: HTMLElement = el;
  while (node.parentElement) {
    const parent = node.parentElement;
    for (let sib = node.nextElementSibling; sib; sib = sib.nextElementSibling) {
      total += (sib as HTMLElement).getBoundingClientRect().height;
    }
    total += parseFloat(window.getComputedStyle(parent).paddingBottom) || 0;
    if (parent === scroller || parent === document.body) break;
    node = parent;
  }
  return total;
}

/**
 * Setzt die Höhe des referenzierten Elements auf den im scrollenden Vorfahren
 * verbleibenden Platz. Robuster als ein fixer `calc(100vh - Npx)`-Offset: ein
 * interner Scroll-Container füllt damit genau den verfügbaren Platz aus, ohne
 * dass die Seite zusätzlich scrollt — der sticky `thead` einer Tabelle bleibt
 * dadurch zuverlässig stehen.
 *
 * Die Messung ist absichtlich **scroll-invariant** (Position im Scroll-Inhalt
 * statt im Viewport): Beim Reiter-Wechsel bleibt die Scrollposition des vorigen
 * Reiters stehen, und eine viewport-relative Messung hätte das Element um genau
 * diesen Versatz zu hoch gemacht — womit sich der Überlauf bei jedem Wechsel
 * weiter aufschaukelt.
 *
 * Reagiert auf Fenster-Resize und auf Layout-Änderungen der Vorfahren-Kette
 * (z. B. wenn der ContactDetails-Header höher wird oder die Speicherleiste
 * erscheint).
 */
export function useFillViewportHeight(
  ref: React.RefObject<HTMLElement>,
  minHeight = 320,
): void {
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || typeof window === "undefined") return;

    // 2px Puffer gegen Subpixel-Rundung — ohne den bleibt gelegentlich ein
    // Ein-Pixel-Scroll übrig, der den sticky Kopf wieder verschieben lässt.
    const PUFFER = 2;

    const apply = () => {
      const scroller = findScrollParent(el);
      const oben = el.getBoundingClientRect().top;
      const reserviert = reservedBelow(el, scroller);
      let verfuegbar: number;
      if (scroller) {
        const rect = scroller.getBoundingClientRect();
        const offsetImInhalt = oben - rect.top - scroller.clientTop + scroller.scrollTop;
        verfuegbar = scroller.clientHeight - offsetImInhalt - reserviert;
      } else {
        verfuegbar = window.innerHeight - oben - reserviert;
      }
      el.style.height = `${Math.max(minHeight, verfuegbar - PUFFER)}px`;
    };

    // Direkt anwenden + im nächsten Frame nachmessen (Fonts/Layout-Shift).
    apply();
    const frame = window.requestAnimationFrame(apply);

    alleFenster.addEventListener("resize", apply);
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(apply) : null;
    if (ro) {
      // Die ganze Kette bis zum Scroller beobachten: eine Änderung auf JEDER
      // Ebene verschiebt die verfügbare Höhe (Kopfbereich wächst, Speicherleiste
      // klappt auf, Chips brechen um).
      const scroller = findScrollParent(el);
      let node: HTMLElement | null = el.parentElement;
      while (node) {
        ro.observe(node);
        if (node === scroller || node === document.body) break;
        node = node.parentElement;
      }
    }

    return () => {
      window.cancelAnimationFrame(frame);
      alleFenster.removeEventListener("resize", apply);
      ro?.disconnect();
    };
  }, [ref, minHeight]);
}
